import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Nfc, Copy, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const canWrite = typeof window !== "undefined" && "NDEFReader" in window;

export const NfcSticker = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["sticker"], queryFn: api.sticker });
  const [writing, setWriting] = useState(false);
  const url = data && `${window.location.origin}/tap/${data.token}`;

  const write = async () => {
    setWriting(true);
    try {
      await new window.NDEFReader().write({ records: [{ recordType: "url", data: url }] });
      toast.success("Sticker ready — anyone who taps it can send you a friend request");
    } catch (e) { toast.error(e?.name === "NotAllowedError" ? "Allow NFC to write the sticker" : "Couldn't write — hold the sticker still against the back of your phone"); }
    setWriting(false);
  };
  const reset = async () => {
    if (!window.confirm("Switch off your old sticker? It will stop working and you'll need to write a new one.")) return;
    try {
      qc.setQueryData(["sticker"], await api.resetSticker());
      toast.success("Old sticker switched off");
    } catch { toast.error("Couldn't reset — try again"); }
  };
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Sticker link copied"));

  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3" data-testid="nfc-sticker">
      <div className="flex items-center gap-2"><Nfc size={18} className="text-emerald-300" /><h3 className="font-semibold">NFC sticker or card</h3></div>
      <p className="text-sm text-slate-300">Put your link on a sticker or card. People tap their phone on it, see your name and photo, and can send you a friend request.</p>
      {canWrite ? (
        <Button onClick={write} disabled={!url || writing} className="w-full bg-emerald-400 text-[#06121c] hover:bg-emerald-300" data-testid="nfc-write-btn">
          <Nfc size={16} /> {writing ? "Hold the sticker to your phone…" : "Write to sticker"}
        </Button>
      ) : (
        <p className="text-xs text-slate-400" data-testid="nfc-unsupported">Writing stickers works in Chrome on Android. On iPhone, copy the link below and write it with a free app like NFC Tools.</p>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={copy} disabled={!url} className="flex-1" data-testid="nfc-copy-link"><Copy size={14} /> Copy sticker link</Button>
        <Button variant="ghost" onClick={reset} disabled={!url} className="text-rose-300 hover:text-rose-200" data-testid="nfc-reset-link"><RotateCcw size={14} /> Reset</Button>
      </div>
      {url && <p className="truncate text-[11px] text-slate-500" data-testid="nfc-link">{url}</p>}
      <p className="text-[11px] text-slate-500">Only your name and photo are shown. Lost the sticker? Tap Reset and it stops working.</p>
    </section>
  );
};
